import { useEffect, useRef, useState } from "react";
import { apiApont } from "../lib/api";
import { MsgModal } from "./MsgModal";
import { Modal, enterTab } from "./EstoqueUi";

// Decisão do Ricardo 25/10/2026: a finalização da última etapa SEM lançamento de produto baixa a matéria-prima empenhada e
// exige o ENCARREGADO (o servidor valida e recusa).
// Reunião 28/10/2026: a SENHA do encarregado (o operador ao lado via a digitação) virou TOKEN TEMPORÁRIO de 6 dígitos que o
// encarregado vê no celular (ERP /meu-token ou Google/Microsoft Authenticator). Aqui: Encarregado + Token. Enter = Tab;
// Enter no token com 6 dígitos confirma.

export interface EncarregadoToken {
  codusu: number;
  token: string;
}

const CHAVE_ULTIMO = "painel_ultimo_encarregado";

/** Encarregado (select) + Token (6 dígitos, só número). Usado na finalização com baixa e no Local de Estoque. */
export function CamposEncarregado({
  valor,
  onChange,
  onEnterToken,
  tokenRef,
  autoFocus = true,
}: {
  valor: EncarregadoToken;
  onChange: (v: EncarregadoToken) => void;
  onEnterToken?: () => void;
  tokenRef?: React.RefObject<HTMLInputElement>;
  autoFocus?: boolean;
}) {
  const [lista, setLista] = useState<{ codusu: number; nomusu: string }[] | null>(null);
  const selRef = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    apiApont
      .encarregados()
      .then((d) => {
        const itens = d.itens ?? [];
        setLista(itens);
        let ult = 0;
        try {
          ult = Number(localStorage.getItem(CHAVE_ULTIMO)) || 0;
        } catch {
          ult = 0;
        }
        const pre = itens.length === 1 ? itens[0]?.codusu ?? 0 : itens.some((i) => i.codusu === ult) ? ult : 0;
        if (pre && !valor.codusu) onChange({ ...valor, codusu: pre });
        if (autoFocus) setTimeout(() => (pre ? tokenRef?.current?.focus() : selRef.current?.focus()), 30);
      })
      .catch(() => setLista([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (lista && lista.length === 0)
    return (
      <p className="pcf-hint" style={{ color: "#b42318", fontWeight: 600 }}>
        Nenhum encarregado com token gerado. No ERP: Cadastros › Usuários › marque “Encarregado” e clique em “Gerar/recriar token”.
      </p>
    );
  return (
    <div className="pcf-grid">
      <label className="pcf-campo">
        <span>Encarregado</span>
        <select
          ref={selRef}
          value={valor.codusu || ""}
          onChange={(e) => {
            const c = Number(e.target.value) || 0;
            try {
              if (c) localStorage.setItem(CHAVE_ULTIMO, String(c));
            } catch {
              /* sem storage: só não lembra o último */
            }
            onChange({ ...valor, codusu: c });
          }}
        >
          <option value=""></option>
          {(lista ?? []).map((l) => (
            <option key={l.codusu} value={l.codusu}>
              {l.codusu} · {l.nomusu}
            </option>
          ))}
        </select>
      </label>
      <label className="pcf-campo pcf-sm">
        <span>Token (celular)</span>
        <input
          ref={tokenRef}
          className="num"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          style={{ letterSpacing: 4, fontWeight: 700 }}
          value={valor.token}
          onChange={(e) => onChange({ ...valor, token: e.target.value.replace(/\D/g, "").slice(0, 6) })}
          onKeyDown={(e) => {
            if (e.key === "Enter" && valor.token.length === 6 && valor.codusu && onEnterToken) {
              e.preventDefault();
              e.stopPropagation();
              onEnterToken();
            }
          }}
        />
      </label>
    </div>
  );
}

interface Props {
  numpcf: number;
  erro: string | null;
  onLimparErro: () => void;
  onConfirmar: (enc: EncarregadoToken) => void;
  onVoltar: () => void;
}

export function SenhaEncarregadoModal({ numpcf, erro, onLimparErro, onConfirmar, onVoltar }: Props) {
  const [enc, setEnc] = useState<EncarregadoToken>({ codusu: 0, token: "" });
  const [aviso, setAviso] = useState<string | null>(null);
  const raiz = useRef<HTMLDivElement>(null);
  const tokenRef = useRef<HTMLInputElement>(null);
  const confirmar = () => {
    if (!enc.codusu) return setAviso("Encarregado não informado!");
    if (enc.token.length !== 6) return setAviso("Token do encarregado não informado (6 dígitos)!");
    onConfirmar(enc);
  };
  return (
    <Modal titulo="Autorização do encarregado" sub={`PCF nº ${numpcf} · Baixa do material empenhado`} largura={520} onClose={onVoltar}>
      <MsgModal
        texto={erro ?? aviso}
        tipo="erro"
        onClose={() => {
          onLimparErro();
          setAviso(null);
          setEnc((v) => ({ ...v, token: "" }));
          setTimeout(() => tokenRef.current?.focus(), 30);
        }}
      />
      <div ref={raiz} onKeyDown={(e) => enterTab(e, raiz.current)}>
        <CamposEncarregado valor={enc} onChange={setEnc} onEnterToken={confirmar} tokenRef={tokenRef} />
        <p className="pcf-hint">O encarregado informa o código que aparece no celular (ERP › Meu token ou Authenticator). Muda a cada 30 s e vale uma vez.</p>
        <div className="pcf-acoes">
          <button className="btn-acao verde" onClick={confirmar}>Confirmar</button>
          <span className="spacer" />
          <button className="btn-secundario" onClick={onVoltar}>Voltar</button>
        </div>
      </div>
    </Modal>
  );
}
